import {
  RegressionSet,
  CreateRegressionSetPayload,
  UpdateRegressionSetPayload,
  RegressionSetItem,
  CreateRegressionSetItemPayload,
  UpdateRegressionSetItemPayload,
  RegressionTestBySetResponse,
  RunRegressionTestByTargetReleasePayload,
  RunRegressionTestBySetPayload,
  AllRegressionTests,
  RegressionTestResultDetail,
  RegressionTestBySetId,
  RegressionSetBySimulatorId,
  CreateRegressionSetFromConversationsPayload,
  RegressionSetFromConversationsResponse,
  AuiRegressionTestJudgeDistinctPair,
  RegressionTestResultsJudgeRow,
} from "./Regression.model";
import { apiResolver, useApi } from "@/api/useApi";

export function useRegressionApi() {
  const { api } = useApi();

  return {
    getRegressionSets: async () =>
      apiResolver<RegressionSet[]>(api.get("/platform/regression-sets")),
    getRegressionSet: async (regressionSetId: string) =>
      apiResolver<RegressionSet>(
        api.get(`/platform/regression-sets/${regressionSetId}`)
      ),
    createRegressionSet: async (input: CreateRegressionSetPayload) =>
      apiResolver<RegressionSet>(api.post("/platform/regression-sets", input)),
    updateRegressionSet: async (
      regressionSetId: string,
      input: UpdateRegressionSetPayload
    ) =>
      apiResolver<RegressionSet>(
        api.put(`/platform/regression-sets/${regressionSetId}`, input)
      ),
    deleteRegressionSet: async (regressionSetId: string) =>
      apiResolver<void>(
        api.delete(`/platform/regression-sets/${regressionSetId}`)
      ),
    createRegressionSetItem: async (
      regressionSetId: string,
      input: CreateRegressionSetItemPayload
    ) =>
      apiResolver<RegressionSetItem>(
        api.post(`/platform/regression-sets/${regressionSetId}/items`, input)
      ),
    updateRegressionSetItem: async (
      itemId: string,
      input: UpdateRegressionSetItemPayload
    ) =>
      apiResolver<RegressionSetItem>(
        api.put(`/platform/regression-sets/items/${itemId}`, input)
      ),
    deleteRegressionSetItem: async (itemId: string) =>
      apiResolver<void>(
        api.delete(`/platform/regression-sets/items/${itemId}`)
      ),
    runRegressionTestBySet: async (input: RunRegressionTestBySetPayload) =>
      apiResolver<RegressionTestBySetResponse>(
        api.post("/platform/regression-test/run-by-test", input)
      ),
    runRegressionTestByTargetRelease: async (
      input: RunRegressionTestByTargetReleasePayload
    ) =>
      apiResolver<RegressionTestBySetResponse>(
        api.post("/platform/regression-test/run", input)
      ),
    getAllRegressionTests: async () =>
      apiResolver<AllRegressionTests[]>(api.get("/platform/regression-test")),
    getRegressionTestResults: async (regressionTestId: string) =>
      apiResolver<RegressionTestResultDetail[]>(
        api.get(`/platform/regression-test/${regressionTestId}/results`)
      ),
    getAllRegressionTestResults: async () =>
      apiResolver<RegressionTestResultDetail[]>(
        api.get("/platform/regression-test/results")
      ),
    getRegressionSetsBySimulatorId: async (simulatorId: string) =>
      apiResolver<RegressionSetBySimulatorId[]>(
        api.get(`/platform/regression-sets/simulators/${simulatorId}`)
      ),
    getRegressionTestsByRegressionSetId: async (regressionSetId: string) =>
      apiResolver<RegressionTestBySetId[]>(
        api.get(`/platform/regression-test/by-set/${regressionSetId}`)
      ),
    getRegressionTestInfo: async (regressionTestId: string) =>
      apiResolver<RegressionTestBySetId>(
        api.get(`/platform/regression-test/${regressionTestId}/info`)
      ),
    createRegressionSetFromConversations: async (
      input: CreateRegressionSetFromConversationsPayload
    ) =>
      apiResolver<RegressionSetFromConversationsResponse>(
        api.post(
          "/platform/regression-sets/positive-feedback/conversations",
          input
        )
      ),
    getDistinctAuiRegressionTestIdPairs: async () =>
      apiResolver<AuiRegressionTestJudgeDistinctPair[]>(
        api.get(
          "/platform/regression-tests-aui/regression-test-results-judge/distinct-pairs"
        )
      ),
    getAuiRegressionTestResultsJudge: async (
      manualRegressionTestId: string,
      auiRegressionTestId: string
    ) =>
      apiResolver<RegressionTestResultsJudgeRow[]>(
        api.get(
          `/platform/regression-tests-aui/regression-test-results-judge`,
          {
            params: {
              manualRegressionTestId,
              auiRegressionTestId,
            },
          }
        )
      ),
  };
}
